import Box from "@mui/material/Box";
import { alpha, styled } from "@mui/material/styles";

import { colors } from "@/theme";

import { EMPTY_SIZES } from "./data";
import { EmptyStateSize } from "./type";

const STILL = "@media (prefers-reduced-motion: reduce)";

/**
 * The mascot, with the sky behind him.
 *
 * The halo is what makes a cut-out of a blue bird legible on a blue page: it
 * puts a pool of brand light under him in the dark scheme and a soft tint in
 * the light one, so he is never floating on nothing.
 */
export const MascotFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "size",
})<{ size: EmptyStateSize }>(({ theme, size }) => {
  const px = EMPTY_SIZES[size];

  return {
    position: "relative",
    width: px,
    height: px,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,

    "&::before": {
      content: '""',
      position: "absolute",
      inset: "-18%",
      borderRadius: "50%",
      background: `radial-gradient(circle, ${alpha(colors.prime, 0.26)} 0%, transparent 68%)`,
      animation: "empty-halo 6s ease-in-out infinite",
    },
    "@keyframes empty-halo": {
      "0%, 100%": { transform: "scale(1)", opacity: 0.75 },
      "50%": { transform: "scale(1.09)", opacity: 1 },
    },
    ...theme.applyStyles("light", {
      "&::before": {
        background: `radial-gradient(circle, ${alpha(colors.prime, 0.16)} 0%, transparent 68%)`,
      },
    }),
    [STILL]: { "&::before": { animation: "none" } },
  };
});

/** Him. Bobbing, because a mascot that holds perfectly still reads as a 404. */
export const MascotImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "round",
})<{ round: boolean }>(({ round }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: round ? "center" : "50% 18%",
  borderRadius: round ? "50%" : 0,
  animation: "empty-float 6s ease-in-out infinite",
  "@keyframes empty-float": {
    "0%, 100%": { transform: "translate3d(0, -4px, 0)" },
    "50%": { transform: "translate3d(0, 6px, 0)" },
  },
  // The full figure keeps its own navy backdrop, so its edges are dissolved
  // into the page rather than cut against it.
  ...(!round && {
    WebkitMaskImage: "radial-gradient(circle, #000 55%, transparent 88%)",
    maskImage: "radial-gradient(circle, #000 55%, transparent 88%)",
  }),
  [STILL]: { animation: "none" },
}));
